import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Tooltip } from 'antd';
import { ThunderboltFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface CreditUsage {
  plan: string | null;
  poolTotal: number;
  poolUsed: number;
  poolRemaining: number;
}

/**
 * Compact chip in the top header showing remaining lead credits.
 * Click navigates to /ai-crm/subscription for details.
 *
 * Color-coded:
 *  - green when >25% remaining
 *  - amber when 25%-10%
 *  - red when <10%
 */
const LeadCreditsChip: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery<CreditUsage>({
    queryKey: ['credits-usage-chip'],
    queryFn: async () => {
      const r = await apiClient.get<{ success: boolean; data: CreditUsage }>('/credits/usage');
      return r.data.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Refetch immediately after a reveal so the counter updates live.
  // LeadDetailModal dispatches 'credits:reveal' on a successful reveal call.
  useEffect(() => {
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['credits-usage-chip'] });
      qc.invalidateQueries({ queryKey: ['credits-usage'] }); // also refresh the Settings panel if open
    };
    window.addEventListener('credits:reveal', handler);
    return () => window.removeEventListener('credits:reveal', handler);
  }, [qc]);

  if (!data || !data.poolTotal) return null;

  const pct = data.poolTotal > 0 ? (data.poolRemaining / data.poolTotal) * 100 : 0;
  let color = '#1B8A4A'; // green
  let bg = 'rgba(34, 139, 87, 0.10)';
  let border = 'rgba(34, 139, 87, 0.28)';
  if (pct < 10) {
    color = '#B42318';
    bg = 'rgba(180, 35, 24, 0.10)';
    border = 'rgba(180, 35, 24, 0.28)';
  } else if (pct < 25) {
    color = '#B54708';
    bg = 'rgba(181, 71, 8, 0.10)';
    border = 'rgba(181, 71, 8, 0.28)';
  }

  const label = `${data.poolRemaining} / ${data.poolTotal}`;

  return (
    <Tooltip title={`Lead credits this period: ${data.poolUsed} used, ${data.poolRemaining} left. Click for details.`}>
      <button
        type="button"
        id="tour-lead-credits"
        onClick={() => navigate('/ai-crm/subscription')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: compact ? '4px 8px' : '5px 12px',
          borderRadius: 999,
          backgroundColor: bg,
          color,
          border: `1px solid ${border}`,
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: '0.02em',
          cursor: 'pointer',
          fontFamily: 'inherit',
          lineHeight: 1.4,
          height: 28,
          whiteSpace: 'nowrap',
        }}
      >
        <ThunderboltFilled style={{ fontSize: 13 }} />
        {compact ? data.poolRemaining : <>{label} <span style={{ opacity: 0.7, fontWeight: 500 }}>leads</span></>}
      </button>
    </Tooltip>
  );
};

export default LeadCreditsChip;
